/**
 * WebSocket Service Types
 *
 * Type definitions for the transactionSubscribe API (via Triton Whirligig)
 * and parsed trade events used throughout the application.
 */

// Meteora Dynamic Bonding Curve (DBC) program ID - for bonding curve phase trades
// NOTE: DLMM (LBUZKhRxPF3XUpBCjp4YzTKgLccjZhTSDM9YuVaPwxo) is for post-graduation pools
export const METEORA_DBC_PROGRAM_ID = 'dbcij3LWUppWqq96dh6gJWwBifmcGfLSB5D4DuSMaqN';
export const WSOL_MINT = 'So11111111111111111111111111111111111111112';

/** Parsed trade event emitted by the WebSocket service */
export interface TradeEvent {
  txSignature: string;
  type: 'BUY' | 'SELL';
  trader: string;
  amountSol: number;
  amountTokens: number;
  timestamp: string;
  tokenMint: string;
  tokenName: string;
  tokenSymbol: string;
  tokenImage: string | null;
}

/** Token metadata used to enrich trade events */
export interface TokenInfo {
  name: string;
  symbol: string;
  imageUrl: string | null;
}

/** Internal subscription tracking */
export interface Subscription {
  id: number;
  subId: number | null;
  accounts: string[];
  callback: (trade: TradeEvent) => void;
  type: 'global' | 'pool';
}

// ---- WebSocket Protocol Types ----

/** transactionSubscribe request filter */
export interface TransactionSubscribeFilter {
  vote?: boolean;
  failed?: boolean;
  signature?: string;
  accountInclude?: string[];
  accountExclude?: string[];
  accountRequired?: string[];
}

/** transactionSubscribe request options */
export interface TransactionSubscribeOptions {
  commitment?: 'processed' | 'confirmed' | 'finalized';
  encoding?: 'base64' | 'base58' | 'jsonParsed';
  transactionDetails?: 'full' | 'signatures' | 'accounts' | 'none';
  showRewards?: boolean;
  maxSupportedTransactionVersion?: number;
}

/** Token balance entry from transaction metadata */
export interface TokenBalanceEntry {
  accountIndex: number;
  mint: string;
  owner?: string;
  programId?: string;
  uiTokenAmount: {
    amount: string;
    decimals: number;
    uiAmount: number | null;
    uiAmountString: string;
  };
}

/** Account key entry from parsed transaction */
export interface AccountKeyEntry {
  pubkey: string;
  signer: boolean;
  writable: boolean;
  source?: string;
}

/** Transaction meta from parsed notification */
export interface TransactionMeta {
  err: unknown | null;
  fee: number;
  preBalances: number[];
  postBalances: number[];
  preTokenBalances: TokenBalanceEntry[];
  postTokenBalances: TokenBalanceEntry[];
}

/** Parsed transaction message */
export interface TransactionMessage {
  accountKeys: AccountKeyEntry[];
  instructions: unknown[];
  recentBlockhash: string;
}

/** Transaction notification result */
export interface TransactionNotificationResult {
  signature: string;
  slot?: number;
  transaction: {
    message: TransactionMessage;
    signatures: string[];
  };
  meta: TransactionMeta;
  blockTime?: number;
}

/** JSON-RPC subscription confirmation */
export interface SubscriptionConfirmation {
  jsonrpc: '2.0';
  id: number;
  result: number;
}

/** JSON-RPC transaction notification */
export interface TransactionNotification {
  jsonrpc: '2.0';
  method: 'transactionNotification';
  params: {
    subscription: number;
    result: TransactionNotificationResult;
  };
}
